/**
 * SMS Firebase web app configuration (ADR-132 Amendment 4).
 *
 * SMS registers with its OWN per-env Firebase Web App entry in the platform
 * `aegix-aim` project — never with `aegix-aim-react`'s entry (the
 * piggyback anti-pattern: config that exists but nobody owns dies silently
 * per env).
 *
 * Env is an app-level boundary from day one (Amendment 4 §2): three Web App
 * entries, env-major display names, the unprefixed name being the production
 * member. The entry display name doubles as the client's `app_identifier`
 * (§3 — web products have no applicationId/bundle id); the backend routes it
 * via exact set membership in `fcm_routing.SMS_APP_IDENTIFIERS`.
 *
 * Per-env values (`apiKey` / `appId` / `measurementId`) are INJECTED through
 * the app's runtime env pattern (`window.__ENV`, emitted by
 * generate-env-config.js from deploy-workflow env vars). When an env
 * carries no values, {@link getSmsFirebaseConfig} fails LOUD (console
 * error) and push registration is skipped gracefully (§4: an entry whose
 * push credential is missing must fail loud, not surface as a generic 401).
 *
 * Project-scoped constants below (sender id, project id, auth domain,
 * storage bucket, VAPID public key) are shared with `aegix-aim-react`'s
 * config because both products live in the `aegix-aim` project; they are
 * NOT secrets (Firebase web config is public by design) and are identical
 * across env entries within the project.
 */

/** `aegix-aim` project sender id — shared by every web app entry in the project. */
export const SMS_FIREBASE_SENDER_ID = '957698080336';

/**
 * `aegix-aim` project Web Push certificate public key (VAPID). Project-scoped
 * in Firebase (Amendment 4 §4) — same pair AIM web uses. If per-product pairs
 * are wanted later, Firebase key-import adds one and this constant changes
 * (client-config-only; fresh tokens mint on next registration).
 */
export const SMS_VAPID_KEY =
  'BGzAORPJWVM7o9SOXXKgbTv9_KmbgzV3KDDzWx_TcKG__PZeCuasDgkSClTonc0GTw-tx6hGZ1glrNUCs2hd6jo';

/** Firebase web app config shape (matches `aegix-aim-react`'s). */
export interface SmsFirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

/**
 * The env-major Firebase Web App entry display name this build registers
 * with (ADR-132 Amendment 4 §2/§3). Derived from the same env axis the app
 * already ships (`NX_SENTRY_ENVIROMENT`: deploy sets `production` /
 * `staging` / `development`; local default is `develop`). Anything that is
 * not explicitly production/staging is a dev build — the dev entry is the
 * safe default because its tokens can never reach prod audiences.
 */
export function getSmsAppIdentifier(): string {
  const env =
    (typeof window !== 'undefined' && window.__ENV?.NX_SENTRY_ENVIROMENT) || '';
  if (env === 'production') return 'aegix-sms-react';
  if (env === 'staging') return 'stage-aegix-sms-react';
  return 'dev-aegix-sms-react';
}

/**
 * Assemble the SMS Firebase web config from injected per-env values, or
 * return `null` (LOUDLY) when the env carries none.
 *
 * `apiKey` + `appId` are the per-entry values that only exist once the
 * env's Web App entry is created in the Firebase console — absent either,
 * there is nothing valid to initialize against and registration must not
 * proceed (initializing with another product's appId is exactly the
 * piggyback failure mode). `measurementId` is optional.
 */
export function getSmsFirebaseConfig(): SmsFirebaseWebConfig | null {
  const env = typeof window !== 'undefined' ? window.__ENV : undefined;
  const apiKey = env?.NX_FIREBASE_API_KEY?.trim();
  const appId = env?.NX_FIREBASE_APP_ID?.trim();

  if (!apiKey || !appId) {
    // Deliberately console.error, not warn/log: a deployed env missing its
    // Firebase entry config is a delivery outage for this product's push
    // surface, and ADR-132 Amendment 4 §4 requires it to fail loud instead
    // of surfacing as opaque downstream 401s.
    console.error(
      `[sms-push] Firebase web app config is ABSENT for this environment ` +
        `(entry "${getSmsAppIdentifier()}"). Push registration is disabled. ` +
        `Expected NX_FIREBASE_API_KEY + NX_FIREBASE_APP_ID via env-config.js ` +
        `(ADR-132 Amendment 4 — SMS per-env Firebase Web App entries; ` +
        `pending console creation + deploy-workflow injection).`,
    );
    return null;
  }

  const measurementId = env?.NX_FIREBASE_MEASUREMENT_ID?.trim();
  return {
    apiKey,
    authDomain: 'aegix-aim.firebaseapp.com',
    projectId: 'aegix-aim',
    storageBucket: 'aegix-aim.appspot.com',
    messagingSenderId: SMS_FIREBASE_SENDER_ID,
    appId,
    ...(measurementId ? { measurementId } : {}),
  };
}
